/**
 * Aspect Admin — i18n (English ⇄ ქართული).
 *
 * English strings are the dictionary keys; t() falls back to the key,
 * so untranslated strings degrade gracefully to English.
 * Placeholders use {name} syntax and are substituted after lookup.
 */

const LANG_KEY = "aspect:portal:lang";

let lang = localStorage.getItem(LANG_KEY) === "ka" ? "ka" : "en";

export function getLang() {
  return lang;
}

export function setLang(next) {
  lang = next === "ka" ? "ka" : "en";
  localStorage.setItem(LANG_KEY, lang);
  document.documentElement.lang = lang;
}

/**
 * Translate a string. @param {string} s @param {Record<string, string|number>} [params]
 */
export function t(s, params) {
  let out = lang === "ka" ? (KA[s] ?? s) : s;
  if (params) {
    for (const [k, v] of Object.entries(params)) out = out.replaceAll(`{${k}}`, String(v));
  }
  return out;
}

/** Markup for the EN/ქა segmented toggle (bound globally in main.js). */
export function langToggle(extraClass = "") {
  return `
    <div class="ap-seg ap-lang ${extraClass}" role="group" aria-label="Language">
      <button type="button" class="ap-seg__btn ${lang === "en" ? "ap-seg__btn--on" : ""}" data-lang="en" title="English">EN</button>
      <button type="button" class="ap-seg__btn ${lang === "ka" ? "ap-seg__btn--on" : ""}" data-lang="ka" title="ქართული">ქარ</button>
    </div>
  `;
}

const KA = {
  /* ---- Navigation & chrome -------------------------------------------------- */
  "Overview": "მიმოხილვა",
  "Spaces": "სივრცეები",
  "AI Jobs": "AI დავალებები",
  "Files": "ფაილები",
  "Analytics": "ანალიტიკა",
  "Clients": "კლიენტები",
  "Activity": "აქტივობა",
  "Team": "გუნდი",
  "Billing": "ბილინგი",
  "Integrations": "ინტეგრაციები",
  "Settings": "პარამეტრები",
  "Audit log": "აუდიტის ჟურნალი",
  "Support": "მხარდაჭერა",
  "Operate": "ოპერაციები",
  "Grow": "ზრდა",
  "Manage": "მართვა",
  "Admin": "ადმინი",
  "{p} plan": "{p} გეგმა",
  "Search or run a command…": "მოძებნე ან გაუშვი ბრძანება…",
  "Ask Aspect": "ჰკითხე Aspect-ს",
  "Notifications": "შეტყობინებები",
  "View all": "ყველას ნახვა",
  "Sign out": "გასვლა",
  "Production": "პროდაქშენი",
  "Workspace switching arrives with multi-workspace support": "სამუშაო სივრცეების გადართვა ხელმისაწვდომი გახდება მულტი-სივრცის მხარდაჭერასთან ერთად",

  /* ---- Command palette ------------------------------------------------------- */
  "Navigate": "ნავიგაცია",
  "Go to {x}": "გადადი: {x}",
  "Actions": "მოქმედებები",
  "Create new space": "ახალი სივრცის შექმნა",
  "Invite team member": "გუნდის წევრის მოწვევა",
  "Generate API key": "API გასაღების გენერაცია",
  "Download latest invoice": "ბოლო ინვოისის ჩამოტვირთვა",
  "Ask Aspect AI…": "ჰკითხე Aspect AI-ს…",
  "Search pages, actions, spaces…": "მოძებნე გვერდები, მოქმედებები, სივრცეები…",
  "No matching commands.": "შესაბამისი ბრძანება ვერ მოიძებნა.",
  "Use “New space” to start a project": "პროექტის დასაწყებად გამოიყენე „ახალი სივრცე“",
  "Invoice INV-2026-0142 downloading…": "ინვოისი INV-2026-0142 იტვირთება…",

  /* ---- AI assistant ------------------------------------------------------------ */
  "Aspect Assistant": "Aspect ასისტენტი",
  "Ask about your workspace, jobs, clients, or usage": "იკითხე სამუშაო სივრცის, დავალებების, კლიენტების ან მოხმარების შესახებ",
  "The workspace is healthy: <strong>2 jobs running</strong>, queue depth 4, and tour sessions are up <strong>8.1%</strong> this month. What would you like to look into?":
    "სამუშაო სივრცე გამართულად მუშაობს: <strong>2 დავალება მიმდინარეობს</strong>, რიგში 4 დავალებაა, ტურის სესიები კი ამ თვეში <strong>8.1%-ით</strong> გაიზარდა. რისი ნახვა გსურთ?",
  "Suggested": "შემოთავაზებული",
  "Why did render volume spike this morning?": "რატომ გაიზარდა რენდერების მოცულობა დღეს დილით?",
  "Which clients are at churn risk?": "რომელი კლიენტებია გადინების რისკის ქვეშ?",
  "Draft a usage summary for the board": "მოამზადე მოხმარების შეჯამება საბჭოსთვის",
  "Ask anything about this workspace…": "იკითხე ნებისმიერი რამ ამ სამუშაო სივრცის შესახებ…",
  "Send": "გაგზავნა",
  "Assistant": "ასისტენტი",
  "This is a demo response. When the AI endpoint is wired, I'll answer with live workspace data — sources and confidence included.":
    "ეს სადემონსტრაციო პასუხია. როდესაც AI ენდფოინთი დაუკავშირდება, გიპასუხებთ სამუშაო სივრცის რეალური მონაცემებით — წყაროებითა და სანდოობის მაჩვენებლით.",
  "View trace": "კვალის ნახვა",
  "Confidence": "სანდოობა",
  "Model confidence {n}%": "მოდელის სანდოობა {n}%",

  /* ---- Statuses & badges ----------------------------------------------------------- */
  "Live": "ლაივი",
  "Active": "აქტიური",
  "Paid": "გადახდილი",
  "Completed": "დასრულებული",
  "Resolved": "მოგვარებული",
  "Running": "მიმდინარე",
  "Rendering": "რენდერდება",
  "Analyzing": "ანალიზდება",
  "Trial": "საცდელი",
  "Invited": "მოწვეული",
  "Queued": "რიგშია",
  "Draft": "მონახაზი",
  "Paused": "შეჩერებული",
  "Pending": "მოლოდინში",
  "Open": "ღია",
  "Needs attention": "ყურადღება სჭირდება",
  "Churn risk": "გადინების რისკი",
  "Failed": "ჩაიშალა",

  /* ---- Common actions & words --------------------------------------------------------- */
  "Search": "ძიება",
  "Export": "ექსპორტი",
  "Download": "ჩამოტვირთვა",
  "Cancel": "გაუქმება",
  "Close": "დახურვა",
  "Save changes": "ცვლილებების შენახვა",
  "Details": "დეტალები",
  "Status": "სტატუსი",
  "Updated": "განახლდა",
  "Created": "შეიქმნა",
  "Delete": "წაშლა",
  "Archive": "არქივირება",
  "Duplicate": "დუბლირება",
  "Copy": "კოპირება",
  "Retry": "გამეორება",
  "Model": "მოდელი",
  "Progress": "პროგრესი",
  "Started": "დაწყება",
  "Client": "კლიენტი",
  "Owner": "მფლობელი",
  "Refresh": "განახლება",
  "rooms": "ოთახი",
  "Other": "სხვა",
  "Test": "ტესტი",
  "Update": "განახლება",
  "Reply": "პასუხი",
  "Date": "თარიღი",
  "View": "ნახვა",
  "Time": "დრო",
  "enabled": "ჩაირთო",
  "disabled": "გამოირთო",

  /* ---- Overview ---------------------------------------------------------------------------- */
  "Good morning": "დილა მშვიდობისა",
  "Good afternoon": "შუადღე მშვიდობისა",
  "Good evening": "საღამო მშვიდობისა",
  "Here's what's moving across <strong>{ws}</strong> today.": "აი, რა ხდება დღეს <strong>{ws}</strong>-ში.",
  "Last 30 days": "ბოლო 30 დღე",
  "New space": "ახალი სივრცე",
  "AI summary": "AI შეჯამება",
  "Momentum is strong: tour sessions are up <strong>8.1%</strong> and AI generations up <strong>23.6%</strong> month over month. The render queue absorbed this morning's Meridian batch without breaching latency targets. <strong>One item needs attention</strong> — the Warehouse Loft plan analysis came back below the confidence threshold.":
    "დინამიკა ძლიერია: ტურის სესიები გაიზარდა <strong>8.1%-ით</strong>, AI გენერაციები კი <strong>23.6%-ით</strong> წინა თვესთან შედარებით. რენდერის რიგმა დილის Meridian-ის პარტია ისე გადაამუშავა, რომ დაყოვნების ლიმიტები არ დარღვეულა. <strong>ერთი საკითხი ყურადღებას საჭიროებს</strong> — Warehouse Loft-ის გეგმის ანალიზი სანდოობის ზღვარს ჩამოსცდა.",
  "Ask a question": "დასვი კითხვა",
  "Active spaces": "აქტიური სივრცეები",
  "Tour sessions": "ტურის სესიები",
  "AI generations": "AI გენერაციები",
  "Median render time": "რენდერის მედიანური დრო",
  "vs last 30 days": "წინა 30 დღესთან შედარებით",
  "faster than last month": "წინა თვეზე სწრაფად",
  "Engagement trend": "ჩართულობის ტრენდი",
  "Tour sessions vs AI generations · weekly": "ტურის სესიები vs AI გენერაციები · ყოველკვირეულად",
  "AI insights": "AI დაკვირვებები",
  "Recent activity": "ბოლო აქტივობა",
  "Active jobs": "აქტიური დავალებები",
  "{n} in queue": "{n} რიგში",
  "Queue p95": "რიგის p95",
  "Success": "წარმატება",
  "Open queue": "რიგის გახსნა",
  "Plan usage": "გეგმის მოხმარება",
  "Usage mix": "მოხმარების სტრუქტურა",
  "jobs / 30d": "დავალება / 30დღ",
  "Quick actions": "სწრაფი მოქმედებები",
  "Start from a plan upload or blank shell": "დაიწყე გეგმის ატვირთვით ან ცარიელი გარსით",
  "Run AI staging": "AI დადგმის გაშვება",
  "Furnish a draft with one prompt": "გაამზადე მონახაზი ერთი პრომპტით",
  "Invite teammate": "კოლეგის მოწვევა",
  "Editors, viewers, or admins": "რედაქტორები, მნახველები ან ადმინები",
  "Share a tour": "ტურის გაზიარება",
  "Copy a client-ready link": "დააკოპირე კლიენტისთვის მზა ბმული",
  "Insights refreshed from the last 30 days of data": "დაკვირვებები განახლდა ბოლო 30 დღის მონაცემებით",
  "Range set to {r}": "დიაპაზონი დაყენდა: {r}",
  "7d": "7დ",
  "30d": "30დ",
  "90d": "90დ",

  /* Insight cards (mock content) */
  "Optimization": "ოპტიმიზაცია",
  "Anomaly": "ანომალია",
  "Forecast": "პროგნოზი",
  "Buyer attention": "მყიდველის ყურადღება",
  "Viewing time": "ყურების დრო",
  "What sells": "რა ყიდის",
  "Kitchen & living room hold 61% of viewing time": "სამზარეულო და მისაღები იკავებს ყურების დროის 61%-ს",
  "Buyers dwell longest in the kitchen and living room, while bathrooms get skimmed in under 8s. Staged rooms hold attention 2.3× longer than empty ones — stage these two spaces first.":
    "მყიდველები ყველაზე დიდხანს სამზარეულოსა და მისაღებში ჩერდებიან, სააბაზანოებს კი 8 წამზე ნაკლებ დროს უთმობენ. მოწყობილი ოთახები ყურადღებას 2.3-ჯერ მეტ ხანს იკავებს, ვიდრე ცარიელი — ჯერ სწორედ ეს ორი სივრცე მოაწყვეთ.",
  "View analytics": "ანალიტიკის ნახვა",
  "Most tours are watched at night, 20:00–23:00": "ტურების უმეტესობას ღამით უყურებენ, 20:00–23:00",
  "44% of sessions start after 20:00, when buyers browse from the couch. Evening viewers stay 38% longer and are 2× more likely to request an in-person viewing the next day.":
    "სესიების 44% იწყება 20:00 საათის შემდეგ, როცა მყიდველები სახლიდან ათვალიერებენ. საღამოს მაყურებლები 38%-ით მეტ ხანს რჩებიან და 2-ჯერ უფრო ხშირად ითხოვენ ადგილზე დათვალიერებას მეორე დღეს.",
  "See sessions": "სესიების ნახვა",
  "Bright, naturally-lit rooms drive the most zoom-ins": "ნათელი, ბუნებრივად განათებული ოთახები იწვევს ყველაზე მეტ მიახლოებას",
  "Daytime-lit renders get 27% more zoom-ins and hotspot clicks than dim shots. Warm evening lighting converts best for bedrooms — mixed lighting keeps buyers exploring longer.":
    "დღის განათებით რენდერები 27%-ით მეტ მიახლოებასა და აქტიური წერტილის დაჭერას იღებენ, ვიდრე ბუნდოვანი კადრები. თბილი საღამოს განათება საუკეთესოდ მუშაობს საძინებლებზე — შერეული განათება მყიდველებს დათვალიერებაში უფრო დიდხანს აჩერებს.",
  "Open analytics": "ანალიტიკის გახსნა",
  "Drop-off": "გასვლის წერტილი",
  "Mobile": "მობილური",
  "Return visits": "განმეორებითი ნახვა",
  "68% exit before the second bedroom": "68% მეორე საძინებლამდე ტურს ტოვებს",
  "Most buyers leave the tour after the living area — they never reach secondary bedrooms. Adding a guided hotspot path through the hallway lifts completion rate by 22%.":
    "მყიდველების უმეტესობა ტურს მისაღების შემდეგ ტოვებს — მეორეული საძინებლებამდე ვერ აღწევენ. დარბაზიდან გაყვანილი ცხელი წერტილების მარშრუტმა დასრულების მაჩვენებელი 22%-ით გაზარდა.",
  "View heatmap": "თერმული რუკის ნახვა",
  "72% of tours open on a phone": "ტურების 72% ტელეფონზე იხსნება",
  "Mobile viewers spend less time per room but return 1.8× more often. Vertical pan in narrow hallways and large tap targets on hotspots keep phone sessions from dropping early.":
    "მობილური მაყურებელი ოთახში ნაკლებ დროს უთმობს, მაგრამ 1.8-ჯერ უფრო ხშირად ბრუნდება. ვიწრო დარბაზებში ვერტიკალური პანორამა და დიდი ცხელი წერტილები სესიის ადრე დასრულებას უშლის ხელს.",
  "See devices": "მოწყობილობების ნახვა",
  "Repeat viewers are 4× more likely to book a viewing": "განმეორებით ნახვა 4-ჯერ უფრო ხშირად იწვევს დათვალიერების ჩაწერას",
  "Buyers who open the same tour twice within 7 days almost always schedule an in-person visit. Send a follow-up link 48h after the first session to catch warm leads.":
    "მყიდველები, რომლებიც 7 დღეში ორჯერ ხსნიან იმავე ტურს, თითქმის ყოველთვის იწერენ ადგილზე ვიზიტს. პირველი სესიიდან 48 საათში გაგზავნილი შეხსენების ბმული თბილ ლიდებს იჭერს.",
  "View leads": "ლიდების ნახვა",

  /* Activity feed (mock content) */
  "<strong>Staging render</strong> completed for <strong>Seaside Villa</strong> · 24 items placed, confidence 0.89":
    "<strong>დადგმის რენდერი</strong> დასრულდა <strong>Seaside Villa</strong>-სთვის · განთავსდა 24 ნივთი, სანდოობა 0.89",
  "<strong>Karin Vaher</strong> uploaded 3 floor plans to <strong>Kastani 14</strong>":
    "<strong>Karin Vaher</strong>-მა ატვირთა 3 გეგმა <strong>Kastani 14</strong>-ში",
  "Tour <strong>Marina Tower 12F</strong> published — link active":
    "ტური <strong>Marina Tower 12F</strong> გამოქვეყნდა — ბმული აქტიურია",
  "<strong>Tomas Berzins</strong> invited <strong>l.kask@meridian.ee</strong> as Editor":
    "<strong>Tomas Berzins</strong>-მა მოიწვია <strong>l.kask@meridian.ee</strong> რედაქტორად",
  "Plan analysis for <strong>Warehouse Loft</strong> flagged low confidence (0.41)":
    "<strong>Warehouse Loft</strong>-ის გეგმის ანალიზმა დაბალი სანდოობა აჩვენა (0.41)",
  "Invoice <strong>INV-2026-0142</strong> paid — €1,240.00":
    "ინვოისი <strong>INV-2026-0142</strong> გადახდილია — €1,240.00",

  /* Usage caps / donut (mock content) */
  "Published tours": "გამოქვეყნებული ტურები",
  "Storage": "საცავი",
  "Team seats": "გუნდის ადგილები",
  "Staging renders": "დადგმის რენდერები",
  "Plan analysis": "გეგმის ანალიზი",
  "Tour exports": "ტურის ექსპორტები",

  /* Trend labels */
  "May 24": "24 მაი",
  "May 31": "31 მაი",
  "Jun 7": "7 ივნ",
  "Jun 14": "14 ივნ",
  "Jun 21": "21 ივნ",
  "Jun 28": "28 ივნ",
  "Jul 4": "4 ივლ",

  /* ---- Spaces --------------------------------------------------------------------------------- */
  "Every apartment, project, and tour across the workspace.": "ყველა ბინა, პროექტი და ტური მთელ სამუშაო სივრცეში.",
  "Import plans": "გეგმების იმპორტი",
  "All": "ყველა",
  "Drafts": "მონახაზები",
  "Processing": "მუშავდება",
  "Attention": "ყურადღება",
  "Search spaces…": "მოძებნე სივრცეები…",
  "Open editor": "რედაქტორის გახსნა",
  "Copy tour link": "ტურის ბმულის კოპირება",
  "Restage with AI": "ხელახალი დადგმა AI-თ",
  "Space": "სივრცე",
  "Rooms": "ოთახები",
  "Area": "ფართობი",
  "Sessions": "სესიები",
  "Assigned": "მინიჭებული",
  "Last updated": "ბოლო განახლება",
  "Tour link": "ტურის ბმული",
  "Recent AI jobs": "ბოლო AI დავალებები",
  "<strong>Tour republished</strong> after material update": "<strong>ტური ხელახლა გამოქვეყნდა</strong> მასალის განახლების შემდეგ",
  "<strong>AI staging</strong> applied Scandinavian theme": "<strong>AI დადგმამ</strong> გამოიყენა სკანდინავიური თემა",
  "<strong>Space created</strong> from plan upload": "<strong>სივრცე შეიქმნა</strong> გეგმის ატვირთვიდან",
  "2 days ago": "2 დღის წინ",
  "Jun 12": "12 ივნ",
  "Share tour": "ტურის გაზიარება",
  "Open in 3D editor": "გახსნა 3D რედაქტორში",
  "Choose how to start. AI can analyze an uploaded floor plan and build the 3D shell automatically.":
    "აირჩიე, როგორ დაიწყო. AI-ს შეუძლია ატვირთული გეგმის ანალიზი და 3D გარსის ავტომატურად აშენება.",
  "Upload 2D plan": "2D გეგმის ატვირთვა",
  "AI analyzes the image, traces walls, and builds the space": "AI აანალიზებს სურათს, მოხაზავს კედლებს და ააშენებს სივრცეს",
  "From demo apartment": "სადემონსტრაციო ბინიდან",
  "Furnished Scandinavian studio, ready to customize": "გაწყობილი სკანდინავიური სტუდია, მზადაა მოსარგებად",
  "From scratch": "ნულიდან",
  "Blank shell — trace the layout yourself": "ცარიელი გარსი — თავად მოხაზე განლაგება",
  "Space name": "სივრცის სახელი",
  "Untitled space": "უსახელო სივრცე",
  "Create & open editor": "შექმნა და რედაქტორის გახსნა",
  "No spaces match": "სივრცეები ვერ მოიძებნა",
  "Try a different filter or search term — or create a new space to get started.":
    "სცადე სხვა ფილტრი ან საძიებო სიტყვა — ან შექმენი ახალი სივრცე დასაწყებად.",
  "Drop floor plans anywhere, or use New space → Upload": "ჩააგდე გეგმები ნებისმიერ ადგილას, ან გამოიყენე „ახალი სივრცე → ატვირთვა“",
  "Tour link copied to clipboard": "ტურის ბმული დაკოპირდა",
  "Tour link copied": "ტურის ბმული დაკოპირდა",
  "AI restage queued for {name}": "AI ხელახალი დადგმა რიგშია: {name}",
  "Duplicated \"{name}\"": "დუბლირებულია „{name}“",
  "\"{name}\" archived": "„{name}“ დაარქივდა",
  "Share link copied to clipboard": "გასაზიარებელი ბმული დაკოპირდა",

  /* Relative times (mock values) */
  "Now": "ახლა",
  "40s ago": "40 წმ წინ",
  "2m ago": "2 წთ წინ",
  "4m ago": "4 წთ წინ",
  "12m ago": "12 წთ წინ",
  "18m ago": "18 წთ წინ",
  "26m ago": "26 წთ წინ",
  "1h ago": "1 სთ წინ",
  "2h ago": "2 სთ წინ",
  "3h ago": "3 სთ წინ",
  "6h ago": "6 სთ წინ",
  "1d ago": "1 დღის წინ",
  "2d ago": "2 დღის წინ",
  "3d ago": "3 დღის წინ",
  "1w ago": "1 კვირის წინ",
  "2w ago": "2 კვირის წინ",
  "Yesterday": "გუშინ",
  "Today": "დღეს",
  "Earlier": "ადრე",
  "26m": "26წთ",
  "38m": "38წთ",
  "1h": "1სთ",
  "2h": "2სთ",
  "5h": "5სთ",
  "1d": "1დღ",
  "2d": "2დღ",

  /* ---- AI Jobs ------------------------------------------------------------------------------------ */
  "Generation queue, processing status, and execution traces.": "გენერაციის რიგი, დამუშავების სტატუსი და შესრულების კვალი.",
  "Pause queue": "რიგის შეჩერება",
  "New generation": "ახალი გენერაცია",
  "All jobs": "ყველა დავალება",
  "Queue depth": "რიგის სიღრმე",
  "{n} jobs": "{n} დავალება",
  "Workers": "ვორკერები",
  "p95 latency": "p95 დაყოვნება",
  "Success rate (7d)": "წარმატების მაჩვენებელი (7დ)",
  "Live · auto-refreshing": "ლაივი · ავტო-განახლება",
  "Job": "დავალება",
  "Error": "შეცდომა",
  "Done": "დასრულდა",
  "Trace": "კვალი",
  "queued #3": "რიგში #3",
  "queued #4": "რიგში #4",
  "Job {id}": "დავალება {id}",
  "Summary": "შეჯამება",
  "Failure reason": "ჩაშლის მიზეზი",
  "Low-resolution source image (640×420). Confidence below 0.5 threshold.":
    "დაბალი გარჩევადობის საწყისი სურათი (640×420). სანდოობა 0.5 ზღვარზე დაბალია.",
  "Retry with enhanced input": "გამეორება გაუმჯობესებული შეყვანით",
  "Execution trace": "შესრულების კვალი",
  "Input received": "შეყვანა მიღებულია",
  "Source image 640×420 · 82 KB": "საწყისი სურათი 640×420 · 82 KB",
  "Preprocessing": "წინასწარი დამუშავება",
  "Upscaled 2× · denoised · contrast normalized": "გადიდდა 2× · მოშორდა ხმაური · კონტრასტი ნორმალიზდა",
  "Wall detection": "კედლების ამოცნობა",
  "Detected 14 segments · 3 ambiguous junctions": "აღმოჩნდა 14 სეგმენტი · 3 გაურკვეველი კვანძი",
  "Confidence gate": "სანდოობის ფილტრი",
  "Score 0.41 — below 0.50 threshold. Job halted before scene build.":
    "ქულა 0.41 — 0.50 ზღვარზე დაბალი. დავალება შეჩერდა სცენის აშენებამდე.",
  "Space \"{s}\" · spec v3": "სივრცე „{s}“ · სპეც v3",
  "Scene graph built": "სცენის გრაფი აშენდა",
  "9 rooms · 42 anchor points resolved": "9 ოთახი · 42 საყრდენი წერტილი გადაწყდა",
  "Model inference": "მოდელის ინფერენცია",
  "{m} · 24 placements proposed": "{m} · შემოთავაზებულია 24 განთავსება",
  "Validation": "ვალიდაცია",
  "Collision check passed · confidence {c}": "შეჯახების შემოწმება გავლილია · სანდოობა {c}",
  "Halted": "შეჩერდა",
  "Review": "განხილვა",
  "Explainability": "ახსნადობა",
  "Every placement decision is logged with its input features and alternatives considered. Full traces are retained for 30 days on the Scale plan.":
    "ყოველი განთავსების გადაწყვეტილება ილოგება შეყვანის მახასიათებლებითა და განხილული ალტერნატივებით. სრული კვალი ინახება 30 დღე Scale გეგმაზე.",
  "Open space": "სივრცის გახსნა",
  "{n} jobs shown": "ნაჩვენებია {n} დავალება",
  "Traces retained 30 days": "კვალი ინახება 30 დღე",
  "Queue paused — running jobs will finish": "რიგი შეჩერდა — მიმდინარე დავალებები დასრულდება",
  "Pick a space to run a generation on": "აირჩიე სივრცე გენერაციის გასაშვებად",
  "Job {id} requeued": "დავალება {id} ხელახლა ჩადგა რიგში",
  "Job {id} requeued with 2× upscaling": "დავალება {id} რიგშია 2× გადიდებით",

  /* Job types (mock) */
  "Staging render": "დადგმის რენდერი",
  "Tour export": "ტურის ექსპორტი",
  "Restyle · Scandinavian": "რესტაილი · სკანდინავიური",

  /* ---- Analytics -------------------------------------------------------------------------------------- */
  "Engagement, performance, and AI usage across all published tours.": "ჩართულობა, წარმადობა და AI მოხმარება ყველა გამოქვეყნებულ ტურზე.",
  "Custom": "ინდივიდუალური",
  "Avg session length": "სესიის საშ. ხანგრძლივობა",
  "Tour completion": "ტურის დასრულება",
  "Unique visitors": "უნიკალური ვიზიტორები",
  "AI cost / render": "AI ღირებულება / რენდერი",
  "vs prior period": "წინა პერიოდთან შედარებით",
  "Sessions over time": "სესიები დროში",
  "All published tours · 30 days": "ყველა გამოქვეყნებული ტური · 30 დღე",
  "Top performing spaces": "საუკეთესო სივრცეები",
  "All spaces": "ყველა სივრცე",
  "Avg time": "საშ. დრო",
  "Completion": "დასრულება",
  "Visitor funnel": "ვიზიტორთა ძაბრი",
  "Tour link opened": "ტურის ბმული გაიხსნა",
  "Entered walkthrough": "შევიდა ტურში",
  "Visited 3+ rooms": "მოინახულა 3+ ოთახი",
  "Completed tour": "დაასრულა ტური",
  "Contacted agent": "დაუკავშირდა აგენტს",
  "Devices": "მოწყობილობები",
  "Desktop": "დესკტოპი",
  "Mobile": "მობილური",
  "Tablet": "ტაბლეტი",
  "visitors": "ვიზიტორი",
  "AI takeaway": "AI დასკვნა",
  "Mobile completion lags desktop by 21 points": "მობილურზე დასრულება დესკტოპს 21 პუნქტით ჩამორჩება",
  "Mobile visitors drop off most in rooms with heavy furniture counts. Consider enabling progressive loading for spaces over 120 m² — projected +8% mobile completion.":
    "მობილური ვიზიტორები ყველაზე ხშირად წყვეტენ ტურს ავეჯით გადატვირთულ ოთახებში. განიხილე პროგრესული ჩატვირთვის ჩართვა 120 მ²-ზე დიდი სივრცეებისთვის — პროგნოზით +8% მობილური დასრულება.",
  "Enable for 3 spaces": "ჩართვა 3 სივრცისთვის",
  "Analytics export queued — you'll get an email": "ანალიტიკის ექსპორტი რიგშია — მიიღებ ელფოსტას",
  "Progressive loading enabled for 3 large spaces": "პროგრესული ჩატვირთვა ჩაირთო 3 დიდი სივრცისთვის",

  /* ---- Clients ------------------------------------------------------------------------------------------- */
  "{n} organizations · {a} active · {t} on trial · {r} at risk": "{n} ორგანიზაცია · {a} აქტიური · {t} საცდელზე · {r} რისკის ქვეშ",
  "Export CSV": "CSV ექსპორტი",
  "Add client": "კლიენტის დამატება",
  "Search clients…": "მოძებნე კლიენტები…",
  "Sort by": "დალაგება",
  "Name": "სახელი",
  "Health": "ჯანმრთელობა",
  "Organization": "ორგანიზაცია",
  "Seats": "ადგილები",
  "Since": "დაწყებიდან",
  "Client since {s} · {n} seats": "კლიენტი {s}-დან · {n} ადგილი",
  "health": "ჯანმრთელობა",
  "Churn signal": "გადინების სიგნალი",
  "No tours published in 34 days and seat activity fell 71%. Similar accounts churned within 6 weeks. Suggested play: schedule a success call and offer an AI staging workshop.":
    "34 დღეა ტური არ გამოქვეყნებულა და ადგილების აქტივობა 71%-ით დაეცა. მსგავსი ანგარიშები 6 კვირაში გავიდნენ. რეკომენდაცია: დანიშნე წარმატების ზარი და შესთავაზე AI დადგმის ვორქშოფი.",
  "Run retention play": "შენარჩუნების გეგმის გაშვება",
  "Account": "ანგარიში",
  "Seats used": "გამოყენებული ადგილები",
  "Primary contact": "ძირითადი კონტაქტი",
  "Region": "რეგიონი",
  "Timeline": "ქრონოლოგია",
  "<strong>Invoice paid</strong> — {m}": "<strong>ინვოისი გადახდილია</strong> — {m}",
  "trial period": "საცდელი პერიოდი",
  "{m} monthly": "{m} თვიურად",
  "<strong>3 new spaces</strong> created by {c}": "<strong>3 ახალი სივრცე</strong> შექმნა {c}-მ",
  "<strong>Account created</strong> · {s}": "<strong>ანგარიში შეიქმნა</strong> · {s}",
  "Internal notes": "შიდა ჩანაწერები",
  "Add a note for your team…": "დაამატე ჩანაწერი გუნდისთვის…",
  "Email contact": "კონტაქტთან მიწერა",
  "Save note": "ჩანაწერის შენახვა",
  "Add client organization": "კლიენტი ორგანიზაციის დამატება",
  "Organization name": "ორგანიზაციის სახელი",
  "Email": "ელფოსტა",
  "Full name": "სრული სახელი",
  "Plan": "გეგმა",
  "Trial (14 days)": "საცდელი (14 დღე)",
  "Send invitation email with portal access": "გაუგზავნე მოსაწვევი ელფოსტა პორტალზე წვდომით",
  "Create client": "კლიენტის შექმნა",
  "{a} of {b} organizations": "{a} / {b} ორგანიზაცია",
  "Health scores refresh nightly": "ჯანმრთელობის ქულები ღამით ახლდება",
  "Client created and invitation sent": "კლიენტი შეიქმნა და მოწვევა გაიგზავნა",
  "CSV export downloading…": "CSV ექსპორტი იტვირთება…",
  "Retention play started — success call scheduled": "შენარჩუნების გეგმა დაიწყო — წარმატების ზარი დაინიშნა",
  "Draft email opened for {c}": "ელფოსტის მონახაზი გაიხსნა {c}-სთვის",
  "Note saved": "ჩანაწერი შენახულია",
  "Jul 1": "1 ივლ",
  "Jun 24": "24 ივნ",
  "Mar 2024": "მარ 2024",
  "Jan 2024": "იან 2024",
  "Sep 2024": "სექ 2024",
  "Jun 2026": "ივნ 2026",
  "Nov 2024": "ნოე 2024",
  "Feb 2025": "თებ 2025",

  /* ---- Team -------------------------------------------------------------------------------------------------- */
  "{a} active members · {p} pending invite · 10 seats on Scale plan": "{a} აქტიური წევრი · {p} მოწვევა მოლოდინში · 10 ადგილი Scale გეგმაზე",
  "Invite member": "წევრის მოწვევა",
  "Members": "წევრები",
  "Roles & permissions": "როლები და უფლებები",
  "Member": "წევრი",
  "Role": "როლი",
  "Security": "უსაფრთხოება",
  "Last active": "ბოლო აქტივობა",
  "Editor": "რედაქტორი",
  "Viewer": "მნახველი",
  "Administrator": "ადმინისტრატორი",
  "No 2FA": "2FA არ აქვს",
  "Resend": "ხელახლა გაგზავნა",
  "Remove member": "წევრის წაშლა",
  "Role changes apply immediately and are recorded in the audit log": "როლის ცვლილებები მაშინვე ძალაში შედის და აუდიტის ჟურნალში ფიქსირდება",
  "View audit log": "აუდიტის ჟურნალის ნახვა",
  "Permission": "უფლება",
  "View spaces & tours": "სივრცეებისა და ტურების ნახვა",
  "Create & edit spaces": "სივრცეების შექმნა და რედაქტირება",
  "Run AI generations": "AI გენერაციების გაშვება",
  "Publish tours": "ტურების გამოქვეყნება",
  "Manage clients": "კლიენტების მართვა",
  "Invite members": "წევრების მოწვევა",
  "Billing & invoices": "ბილინგი და ინვოისები",
  "API keys & webhooks": "API გასაღებები და ვებჰუკები",
  "Workspace settings": "სამუშაო სივრცის პარამეტრები",
  "Custom roles are available on the Enterprise plan": "მორგებული როლები ხელმისაწვდომია Enterprise გეგმაზე",
  "Talk to us": "დაგვიკავშირდი",
  "Email addresses": "ელფოსტის მისამართები",
  "name@company.com, another@company.com": "name@company.com, another@company.com",
  "Separate multiple addresses with commas. 5 seats remaining on your plan.": "რამდენიმე მისამართი გამოყავი მძიმეებით. გეგმაზე დარჩენილია 5 ადგილი.",
  "Editor — create spaces, run AI jobs": "რედაქტორი — ქმნის სივრცეებს, უშვებს AI დავალებებს",
  "Admin — manage clients, keys, and members": "ადმინისტრატორი — მართავს კლიენტებს, გასაღებებსა და წევრებს",
  "Viewer — read-only access": "მნახველი — მხოლოდ ნახვის წვდომა",
  "Personal message": "პირადი შეტყობინება",
  "(optional)": "(არასავალდებულო)",
  "Welcome to our Aspect workspace…": "კეთილი იყოს შენი მობრძანება ჩვენს Aspect სამუშაო სივრცეში…",
  "Send invitations": "მოწვევების გაგზავნა",
  "Invitations sent": "მოწვევები გაიგზავნა",
  "Role updated to {r}": "როლი განახლდა: {r}",
  "Invitation resent to {e}": "მოწვევა ხელახლა გაეგზავნა: {e}",
  "{n} removed from workspace": "{n} წაიშალა სამუშაო სივრციდან",
  "We'll reach out about Enterprise roles": "დაგიკავშირდებით Enterprise როლების შესახებ",

  /* ---- Billing ------------------------------------------------------------------------------------------------- */
  "Subscription, usage caps, and invoice history.": "გამოწერა, მოხმარების ლიმიტები და ინვოისების ისტორია.",
  "Customer portal": "მომხმარებლის პორტალი",
  "Scale plan": "Scale გეგმა",
  "/ month · billed monthly": "/ თვეში · ყოველთვიური გადახდა",
  "Includes 3,000 AI generations, 150 published tours, 1 TB asset storage, and 10 team seats. Renews <strong>Aug 1, 2026</strong>.":
    "მოიცავს 3,000 AI გენერაციას, 150 გამოქვეყნებულ ტურს, 1 ტბ საცავს და 10 გუნდის ადგილს. განახლდება <strong>2026 წლის 1 აგვისტოს</strong>.",
  "Change plan": "გეგმის შეცვლა",
  "Invoices": "ინვოისები",
  "Download all": "ყველას ჩამოტვირთვა",
  "Invoice": "ინვოისი",
  "Amount": "თანხა",
  "Usage this cycle": "მოხმარება ამ ციკლში",
  "Resets Aug 1": "განულდება 1 აგვ",
  "You're on track to stay within plan": "გეგმის ფარგლებში რჩები",
  "Projected end-of-cycle usage: 2,430 generations (81%). If the Meridian batch cadence doubles, consider the annual Scale plan — it would save <strong>€2,976 / year</strong>.":
    "პროგნოზირებული მოხმარება ციკლის ბოლოს: 2,430 გენერაცია (81%). თუ Meridian-ის პარტიების ტემპი გაორმაგდება, განიხილე წლიური Scale გეგმა — დაზოგავდი <strong>€2,976 წელიწადში</strong>.",
  "See annual pricing": "წლიური ფასების ნახვა",
  "Payment method": "გადახდის მეთოდი",
  "1,500 AI generations · 75 tours · 512 GB": "1,500 AI გენერაცია · 75 ტური · 512 გბ",
  "3,000 AI generations · 150 tours · 1 TB": "3,000 AI გენერაცია · 150 ტური · 1 ტბ",
  "Unlimited usage · SSO · custom roles · SLA": "შეუზღუდავი მოხმარება · SSO · მორგებული როლები · SLA",
  "Current": "მიმდინარე",
  "annual": "წლიური",
  "/ month": "/ თვეში",
  "Talk to sales": "გაყიდვებთან საუბარი",
  "We'll reach out within one business day": "დაგიკავშირდებით ერთ სამუშაო დღეში",
  "Opening secure customer portal…": "იხსნება უსაფრთხო მომხმარებლის პორტალი…",
  "We'd hate to see you go — a specialist will reach out": "გვეწყინება შენი წასვლა — სპეციალისტი დაგიკავშირდება",
  "Invoice archive downloading…": "ინვოისების არქივი იტვირთება…",
  "Payment method update requires re-authentication": "გადახდის მეთოდის განახლება ხელახალ ავთენტიფიკაციას საჭიროებს",
  "{id}.pdf downloading…": "{id}.pdf იტვირთება…",
  "Jul 1, 2026": "1 ივლ, 2026",
  "Jun 1, 2026": "1 ივნ, 2026",
  "May 1, 2026": "1 მაი, 2026",
  "Apr 1, 2026": "1 აპრ, 2026",
  "Mar 1, 2026": "1 მარ, 2026",

  /* ---- Integrations ------------------------------------------------------------------------------------------------ */
  "Connected apps, API access, and event webhooks.": "დაკავშირებული აპები, API წვდომა და მოვლენების ვებჰუკები.",
  "API documentation": "API დოკუმენტაცია",
  "Apps": "აპები",
  "API keys": "API გასაღებები",
  "Webhooks": "ვებჰუკები",
  "Connected": "დაკავშირებული",
  "Not connected": "არ არის დაკავშირებული",
  "Configure": "კონფიგურაცია",
  "Disconnect": "გათიშვა",
  "Connect": "დაკავშირება",
  "Job completions, publish events, and AI alerts in your channels.": "დავალებების დასრულება, გამოქვეყნების მოვლენები და AI შეტყობინებები შენს არხებში.",
  "Embed published tours directly into client-facing listing pages.": "ჩასვი გამოქვეყნებული ტურები პირდაპირ კლიენტის განცხადებების გვერდებზე.",
  "Sync client organizations and tour engagement to CRM contacts.": "დაასინქრონე კლიენტი ორგანიზაციები და ტურის ჩართულობა CRM კონტაქტებთან.",
  "Automate workflows across 6,000+ apps when portal events fire.": "ავტომატიზებული სამუშაო ნაკადები 6,000+ აპში პორტალის მოვლენებზე.",
  "Import floor plans and export tour assets to shared drives.": "შემოიტანე გეგმები და გაიტანე ტურის რესურსები საზიარო დისკებზე.",
  "Pull existing scans as base geometry for AI staging.": "გამოიყენე არსებული სკანები საბაზისო გეომეტრიად AI დადგმისთვის.",
  "Authenticate server-to-server requests against the Aspect API": "სერვერიდან სერვერზე მოთხოვნების ავთენტიფიკაცია Aspect API-სთვის",
  "Generate key": "გასაღების გენერაცია",
  "Key": "გასაღები",
  "Token": "ტოკენი",
  "Scopes": "უფლებები",
  "Last used": "ბოლო გამოყენება",
  "Production key": "პროდაქშენის გასაღები",
  "Staging key": "სთეიჯინგის გასაღები",
  "Full access": "სრული წვდომა",
  "Read + jobs": "კითხვა + დავალებები",
  "Rotate": "როტაცია",
  "Revoke": "გაუქმება",
  "Keys grant programmatic access — store them in a secrets manager, never in code": "გასაღებები პროგრამულ წვდომას იძლევა — შეინახე საიდუმლოების მენეჯერში, არასდროს კოდში",
  "API docs": "API დოკუმენტაცია",
  "Webhook endpoints": "ვებჰუკის ენდფოინთები",
  "Receive real-time events when jobs finish, tours publish, or spaces change": "მიიღე რეალურ დროში მოვლენები, როცა დავალებები სრულდება, ტურები ქვეყნდება ან სივრცეები იცვლება",
  "Add endpoint": "ენდფოინთის დამატება",
  "Endpoint": "ენდფოინთი",
  "Events": "მოვლენები",
  "Delivery rate": "მიწოდების მაჩვენებელი",
  "Deliveries are signed with your webhook secret · retried 5× with backoff": "მიწოდებები ხელმოწერილია ვებჰუკის საიდუმლოთი · მეორდება 5-ჯერ დაყოვნებით",
  "Reveal signing secret": "ხელმოწერის საიდუმლოს ჩვენება",
  "Key label": "გასაღების სახელი",
  "e.g. CI pipeline": "მაგ. CI პაიპლაინი",
  "Read spaces & tours": "სივრცეებისა და ტურების კითხვა",
  "Create AI jobs": "AI დავალებების შექმნა",
  "Manage webhooks": "ვებჰუკების მართვა",
  "Full admin access": "სრული ადმინ წვდომა",
  "The full token is shown once after generation. Store it securely.": "სრული ტოკენი მხოლოდ ერთხელ გამოჩნდება გენერაციის შემდეგ. შეინახე უსაფრთხოდ.",
  "Generate": "გენერაცია",
  "Redirecting to {n} OAuth…": "გადამისამართება {n} OAuth-ზე…",
  "{n} disconnected": "{n} გაითიშა",
  "{n} settings opening…": "{n} პარამეტრები იხსნება…",
  "Key prefix copied": "გასაღების პრეფიქსი დაკოპირდა",
  "Key rotated — update your services": "გასაღები განახლდა — განაახლე სერვისები",
  "Key revoked immediately": "გასაღები მაშინვე გაუქმდა",
  "Test event delivered · 200 OK in 184ms": "სატესტო მოვლენა მიწოდებულია · 200 OK 184მწ-ში",
  "Webhook endpoint deleted": "ვებჰუკის ენდფოინთი წაიშალა",
  "Key generated — copied to clipboard": "გასაღები დაგენერირდა — დაკოპირდა",
  "Enter an HTTPS endpoint to receive events": "შეიყვანე HTTPS ენდფოინთი მოვლენების მისაღებად",
  "Re-authenticate to reveal the signing secret": "ხელმოწერის საიდუმლოს სანახავად ხელახლა გაიარე ავთენტიფიკაცია",

  /* ---- Activity center ------------------------------------------------------------------------------------------------- */
  "{n} unread notifications across the workspace.": "{n} წაუკითხავი შეტყობინება სამუშაო სივრცეში.",
  "Notification settings": "შეტყობინებების პარამეტრები",
  "Mark all read": "ყველას წაკითხულად მონიშვნა",
  "AI & jobs": "AI და დავალებები",
  "All clear": "ყველაფერი წესრიგშია",
  "No notifications in this category. New events will show up here in real time.": "ამ კატეგორიაში შეტყობინებები არ არის. ახალი მოვლენები აქ რეალურ დროში გამოჩნდება.",
  "All notifications marked read": "ყველა შეტყობინება მოინიშნა წაკითხულად",
  "Staging render completed": "დადგმის რენდერი დასრულდა",
  "Seaside Villa — 24 items placed with 0.89 confidence.": "Seaside Villa — განთავსდა 24 ნივთი 0.89 სანდოობით.",
  "Low confidence analysis": "დაბალი სანდოობის ანალიზი",
  "Warehouse Loft plan scored 0.41 — a higher-resolution image is recommended.": "Warehouse Loft-ის გეგმამ მიიღო 0.41 — რეკომენდებულია უფრო მაღალი გარჩევადობის სურათი.",
  "Invitation accepted": "მოწვევა მიღებულია",
  "Marta Liiv joined the workspace as Editor.": "Marta Liiv შემოუერთდა სამუშაო სივრცეს რედაქტორად.",
  "Payment received": "გადახდა მიღებულია",
  "Invoice INV-2026-0142 (€1,240.00) was paid via SEPA.": "ინვოისი INV-2026-0142 (€1,240.00) გადახდილია SEPA-თი.",
  "Queue autoscaled": "რიგი ავტომატურად გაფართოვდა",
  "Worker pool expanded 4 → 8 to absorb the Meridian batch upload.": "ვორკერების რაოდენობა გაიზარდა 4 → 8 Meridian-ის პარტიის ასათვისებლად.",
  "New sign-in": "ახალი შესვლა",
  "Tomas Berzins signed in from Tallinn, EE (Chrome / macOS).": "Tomas Berzins შევიდა ტალინიდან, EE (Chrome / macOS).",

  /* ---- Files ------------------------------------------------------------------------------------------------------------ */
  "Floor plans, photos, 3D exports, and brand assets across the workspace.": "გეგმები, ფოტოები, 3D ექსპორტები და ბრენდის რესურსები მთელ სამუშაო სივრცეში.",
  "Upload files": "ფაილების ატვირთვა",
  "Drop files here": "ჩააგდე ფაილები აქ",
  "or click to browse": "ან დააკლიკე ასარჩევად",
  "Floor plans (PNG, JPG, PDF, DWG) · photos · 3D models up to 250 MB": "გეგმები (PNG, JPG, PDF, DWG) · ფოტოები · 3D მოდელები 250 MB-მდე",
  "All files": "ყველა ფაილი",
  "Floor plans": "გეგმები",
  "Photos": "ფოტოები",
  "3D exports": "3D ექსპორტები",
  "Documents": "დოკუმენტები",
  "Floor plan": "გეგმა",
  "Photo": "ფოტო",
  "3D export": "3D ექსპორტი",
  "Document": "დოკუმენტი",
  "Brand asset": "ბრენდის რესურსი",
  "CAD file": "CAD ფაილი",
  "Search files…": "მოძებნე ფაილები…",
  "File": "ფაილი",
  "Uploaded by": "ატვირთა",
  "Workspace": "სამუშაო სივრცე",
  "{n} files": "{n} ფაილი",
  "Deleted files are recoverable for 30 days": "წაშლილი ფაილების აღდგენა შესაძლებელია 30 დღის განმავლობაში",
  "Asset storage": "რესურსების საცავი",
  "Housekeeping": "დალაგება",
  "64 GB of unused exports found": "ნაპოვნია 64 გბ გამოუყენებელი ექსპორტი",
  "11 tour exports haven't been accessed in 90+ days and their spaces have newer versions. Archiving them frees 6% of your storage.":
    "11 ტურის ექსპორტს 90+ დღეა არავინ შეხებია და მათ სივრცეებს უფრო ახალი ვერსიები აქვთ. მათი არქივირება საცავის 6%-ს გაათავისუფლებს.",
  "Archive 11 files": "11 ფაილის არქივირება",
  "Type": "ტიპი",
  "Size": "ზომა",
  "Linked space": "დაკავშირებული სივრცე",
  "uploaded": "აიტვირთა",
  "Uploaded": "აიტვირთა",
  "AI ready": "AI მზადაა",
  "This plan can be analyzed to generate a 3D space automatically. Estimated processing time: ~90 seconds.":
    "ამ გეგმის ანალიზით შესაძლებელია 3D სივრცის ავტომატურად შექმნა. სავარაუდო დამუშავების დრო: ~90 წამი.",
  "Analyze plan": "გეგმის ანალიზი",
  "Upload started — files appear here when processed": "ატვირთვა დაიწყო — ფაილები აქ გამოჩნდება დამუშავების შემდეგ",
  "11 files archived — 64 GB freed": "11 ფაილი დაარქივდა — გათავისუფლდა 64 გბ",
  "{f} downloading…": "{f} იტვირთება…",
  "{f} moved to trash": "{f} გადავიდა ურნაში",
  "Plan analysis queued — track it in AI Jobs": "გეგმის ანალიზი რიგშია — თვალი ადევნე AI დავალებებში",
  "AI Engine": "AI ძრავა",

  /* ---- Settings ----------------------------------------------------------------------------------------------------------- */
  "Workspace configuration, branding, security, and preferences.": "სამუშაო სივრცის კონფიგურაცია, ბრენდინგი, უსაფრთხოება და პრეფერენციები.",
  "Branding": "ბრენდინგი",
  "Preferences": "პრეფერენციები",
  "General": "ზოგადი",
  "Workspace name": "სამუშაო სივრცის სახელი",
  "Workspace URL": "სამუშაო სივრცის URL",
  "Default region": "ნაგულისხმევი რეგიონი",
  "Default tour visibility": "ტურის ნაგულისხმევი ხილვადობა",
  "EU West (Frankfurt)": "ევროპა დასავლეთი (ფრანკფურტი)",
  "EU North (Stockholm)": "ევროპა ჩრდილოეთი (სტოკჰოლმი)",
  "US East (Virginia)": "აშშ აღმოსავლეთი (ვირჯინია)",
  "Link-only": "მხოლოდ ბმულით",
  "Private": "პირადი",
  "Public": "საჯარო",
  "Changes apply to new spaces only": "ცვლილებები მხოლოდ ახალ სივრცეებზე ვრცელდება",
  "Danger zone": "სახიფათო ზონა",
  "Transfer ownership": "მფლობელობის გადაცემა",
  "Delete workspace": "სამუშაო სივრცის წაშლა",
  "Hand this workspace to another owner. Requires their confirmation.": "გადაეცი ეს სამუშაო სივრცე სხვა მფლობელს. საჭიროებს მის დადასტურებას.",
  "Permanently removes all spaces, tours, files, and members. Cannot be undone.": "სამუდამოდ შლის ყველა სივრცეს, ტურს, ფაილსა და წევრს. ვერ დაბრუნდება.",
  "Tour branding": "ტურის ბრენდინგი",
  "Applied to every published tour and client-facing page": "ვრცელდება ყველა გამოქვეყნებულ ტურსა და კლიენტის გვერდზე",
  "Logo": "ლოგო",
  "Upload logo": "ლოგოს ატვირთვა",
  "SVG or PNG, min 240px": "SVG ან PNG, მინ. 240px",
  "Accent color": "აქცენტის ფერი",
  "Custom domain": "საკუთარი დომენი",
  "Point a CNAME at tours.aspect.app, then verify here.": "მიმართე CNAME ჩანაწერი tours.aspect.app-ზე და აქ დაადასტურე.",
  "Preview updates on the next publish": "გადახედვა განახლდება შემდეგ გამოქვეყნებაზე",
  "Save branding": "ბრენდინგის შენახვა",
  "Show Aspect watermark": "Aspect-ის წყლის ნიშნის ჩვენება",
  "Display “Powered by Aspect” on published tours. Removable on Scale and above.": "აჩვენე „Powered by Aspect“ გამოქვეყნებულ ტურებზე. მოხსნადია Scale გეგმიდან.",
  "Client-branded emails": "კლიენტის ბრენდით ელფოსტები",
  "Send tour share emails from your domain with your logo.": "გააგზავნე ტურის გაზიარების ელფოსტები შენი დომენიდან შენი ლოგოთი.",
  "Access & authentication": "წვდომა და ავთენტიფიკაცია",
  "Require two-factor authentication": "ორფაქტორიანი ავთენტიფიკაციის მოთხოვნა",
  "All members must enroll a second factor before accessing the workspace.": "ყველა წევრმა უნდა დაარეგისტრიროს მეორე ფაქტორი სამუშაო სივრცეში შესვლამდე.",
  "Single sign-on (SAML)": "ერთიანი შესვლა (SAML)",
  "Authenticate through your identity provider. Available on Enterprise.": "ავთენტიფიკაცია შენი იდენტობის პროვაიდერით. ხელმისაწვდომია Enterprise-ზე.",
  "Session timeout": "სესიის ვადა",
  "Sign members out after 12 hours of inactivity.": "წევრების გამოსვლა 12 საათი უმოქმედობის შემდეგ.",
  "Data protection": "მონაცემთა დაცვა",
  "IP allowlist": "IP დაშვებული სია",
  "Restrict portal access to approved network ranges.": "შეზღუდე პორტალზე წვდომა დამტკიცებული ქსელური დიაპაზონებით.",
  "Audit log streaming": "აუდიტის ჟურნალის სტრიმინგი",
  "Stream security events to your SIEM via webhook.": "გადაეცი უსაფრთხოების მოვლენები შენს SIEM-ს ვებჰუკით.",
  "Data residency": "მონაცემთა ადგილმდებარეობა",
  "All workspace data is stored and processed in <strong>EU West (Frankfurt)</strong>.": "სამუშაო სივრცის ყველა მონაცემი ინახება და მუშავდება <strong>ევროპა დასავლეთში (ფრანკფურტი)</strong>.",
  "Active sessions": "აქტიური სესიები",
  "Tallinn, EE · current session": "ტალინი, EE · მიმდინარე სესია",
  "Tallinn, EE · 2 hours ago": "ტალინი, EE · 2 საათის წინ",
  "Riga, LV · 3 days ago": "რიგა, LV · 3 დღის წინ",
  "This device": "ეს მოწყობილობა",
  "AI job completions": "AI დავალებების დასრულება",
  "Notify when generations finish or fail.": "შეტყობინება, როცა გენერაციები სრულდება ან იშლება.",
  "Client activity": "კლიენტების აქტივობა",
  "Tour views, engagement spikes, and churn signals.": "ტურის ნახვები, ჩართულობის ნახტომები და გადინების სიგნალები.",
  "Billing alerts": "ბილინგის შეტყობინებები",
  "Usage past 80% of any cap, invoices, and payment issues.": "მოხმარება ლიმიტის 80%-ზე მეტი, ინვოისები და გადახდის პრობლემები.",
  "Weekly digest email": "ყოველკვირეული შეჯამების ელფოსტა",
  "A Monday summary of workspace performance.": "ორშაბათის შეჯამება სამუშაო სივრცის შედეგებზე.",
  "AI behavior": "AI ქცევა",
  "Proactive insights": "პროაქტიული დაკვირვებები",
  "Let Aspect surface anomalies and recommendations on the dashboard.": "მიეცი Aspect-ს საშუალება დაფაზე აჩვენოს ანომალიები და რეკომენდაციები.",
  "Auto-retry failed jobs": "ჩაშლილი დავალებების ავტო-გამეორება",
  "Retry failed generations once with enhanced preprocessing.": "გაიმეორე ჩაშლილი გენერაციები ერთხელ გაუმჯობესებული წინასწარი დამუშავებით.",
  "Confidence threshold": "სანდოობის ზღვარი",
  "Jobs scoring below this value are flagged for review instead of auto-completing.": "ამ მნიშვნელობაზე დაბალი ქულის დავალებები მოინიშნება განსახილველად ავტომატური დასრულების ნაცვლად.",
  "0.50 — balanced": "0.50 — დაბალანსებული",
  "0.70 — strict": "0.70 — მკაცრი",
  "0.30 — permissive": "0.30 — ლმობიერი",
  "Settings saved": "პარამეტრები შენახულია",
  "Session revoked": "სესია გაუქმდა",
  "\"{t}\" requires owner re-authentication": "„{t}“ საჭიროებს მფლობელის ხელახალ ავთენტიფიკაციას",
  "Logo upload opening…": "ლოგოს ატვირთვა იხსნება…",
  "Language": "ენა",

  /* ---- Audit ---------------------------------------------------------------------------------------------------------------- */
  "Immutable record of every administrative and system event.": "ყველა ადმინისტრაციული და სისტემური მოვლენის უცვლელი ჩანაწერი.",
  "Stream to SIEM": "სტრიმინგი SIEM-ში",
  "Info": "ინფო",
  "Warning": "გაფრთხილება",
  "Warnings": "გაფრთხილებები",
  "Search actor, action, detail…": "მოძებნე მოქმედი, ქმედება, დეტალი…",
  "Severity": "სიმძიმე",
  "Action": "ქმედება",
  "Actor": "მოქმედი",
  "Detail": "დეტალი",
  "Event": "მოვლენა",
  "Source IP": "წყაროს IP",
  "Timestamp": "დროის ნიშნული",
  "Raw payload": "ნედლი მონაცემები",
  "Copy event JSON": "მოვლენის JSON-ის კოპირება",
  "{n} events · retained 12 months on Scale": "{n} მოვლენა · ინახება 12 თვე Scale-ზე",
  "Events are cryptographically chained and tamper-evident": "მოვლენები კრიპტოგრაფიულად ჯაჭვურია და ცვლილება შესამჩნევია",
  "Audit export queued — link arrives by email": "აუდიტის ექსპორტი რიგშია — ბმული ელფოსტით მოვა",
  "Event JSON copied": "მოვლენის JSON დაკოპირდა",
  "System": "სისტემა",
  "Unknown": "უცნობი",
  "internal": "შიდა",
  "Changed default tour visibility: private → link-only": "შეიცვალა ტურის ნაგულისხმევი ხილვადობა: პირადი → მხოლოდ ბმულით",
  "Plan analysis job_2qd4 — confidence 0.41 below threshold": "გეგმის ანალიზი job_2qd4 — სანდოობა 0.41 ზღვარზე დაბალია",
  "Rotated staging key asp_test_2ba…": "განახლდა სთეიჯინგის გასაღები asp_test_2ba…",
  "Worker pool 4 → 8 (load threshold exceeded)": "ვორკერების რაოდენობა 4 → 8 (დატვირთვის ზღვარი გადაჭარბდა)",
  "Invited l.kask@meridian.ee with role Viewer": "მოწვეულია l.kask@meridian.ee მნახველის როლით",
  "3 failed sign-in attempts for anders@aspect.app": "3 წარუმატებელი შესვლის მცდელობა anders@aspect.app-ზე",
  "Published Marina Tower 12F (link-only)": "გამოქვეყნდა Marina Tower 12F (მხოლოდ ბმულით)",
  "INV-2026-0142 · €1,240.00 via SEPA": "INV-2026-0142 · €1,240.00 SEPA-თი",
  "Today 09:42": "დღეს 09:42",
  "Today 09:18": "დღეს 09:18",
  "Yesterday 17:03": "გუშინ 17:03",
  "Yesterday 09:40": "გუშინ 09:40",
  "Jul 2 14:22": "2 ივლ 14:22",
  "Jul 2 03:11": "2 ივლ 03:11",
  "Jul 1 16:48": "1 ივლ 16:48",
  "Jul 1 08:00": "1 ივლ 08:00",

  /* ---- Support ----------------------------------------------------------------------------------------------------------------- */
  "{n} open conversations · median first response 42 minutes.": "{n} ღია საუბარი · პირველი პასუხის მედიანა 42 წუთი.",
  "Documentation": "დოკუმენტაცია",
  "New ticket": "ახალი ტიკეტი",
  "Client conversations": "კლიენტების საუბრები",
  "Subject": "თემა",
  "From": "ვისგან",
  "Priority": "პრიორიტეტი",
  "Assignee": "პასუხისმგებელი",
  "High": "მაღალი",
  "Medium": "საშუალო",
  "Low": "დაბალი",
  "Textures look washed out on exported tours": "ტექსტურები გაფერმკრთალებულად ჩანს ექსპორტირებულ ტურებზე",
  "Request: bulk CSV import for space metadata": "მოთხოვნა: სივრცის მეტამონაცემების ჯგუფური CSV იმპორტი",
  "Tour link 404s after re-publish": "ტურის ბმული 404-ს აბრუნებს ხელახალი გამოქვეყნების შემდეგ",
  "How to add custom watermark per client?": "როგორ დავამატო საკუთარი წყლის ნიშანი თითო კლიენტზე?",
  "{f} · updated {u}": "{f} · განახლდა {u}",
  "Conversation": "საუბარი",
  "Hi — {s}. Could you take a look? This affects our client demos scheduled for next week.":
    "გამარჯობა — {s}. შეგიძლიათ ნახოთ? ეს გავლენას ახდენს მომავალ კვირაზე დაგეგმილ კლიენტების დემოებზე.",
  "Suggested reply": "შემოთავაზებული პასუხი",
  "Thanks for flagging this. We've reproduced the issue and a fix is rolling out with tomorrow's release. As a workaround, re-exporting the tour with \"high quality textures\" enabled resolves the washed-out look.":
    "მადლობა შეტყობინებისთვის. პრობლემა აღვადგინეთ და გამოსწორება ხვალინდელ რელიზში გავა. დროებით გამოსავალად, ტურის ხელახალი ექსპორტი ჩართული „მაღალი ხარისხის ტექსტურებით“ აგვარებს გაფერმკრთალებას.",
  "Use this reply": "ამ პასუხის გამოყენება",
  "Visible to your team only…": "ხილულია მხოლოდ შენი გუნდისთვის…",
  "Likely related to the tone-mapping change in v2.14.": "სავარაუდოდ დაკავშირებულია v2.14-ის ტონ-მეპინგის ცვლილებასთან.",
  "Properties": "თვისებები",
  "Mark resolved": "მოგვარებულად მონიშვნა",
  "Send us feedback": "გამოგვიგზავნე უკუკავშირი",
  "What's on your mind?": "რა გაწუხებს?",
  "Feature request": "ფუნქციის მოთხოვნა",
  "Bug report": "ხარვეზის შეტყობინება",
  "Pricing question": "ფასების კითხვა",
  "Something else": "სხვა რამ",
  "Tell us what would make Aspect better for your team…": "გვითხარი, რა გახდიდა Aspect-ს უკეთესს შენი გუნდისთვის…",
  "Send feedback": "უკუკავშირის გაგზავნა",
  "Resources": "რესურსები",
  "Getting started guide": "დაწყების გზამკვლევი",
  "Set up your first AI-staged tour": "შექმენი პირველი AI-დადგმული ტური",
  "3D editor handbook": "3D რედაქტორის სახელმძღვანელო",
  "Tracing, staging, and publishing": "მოხაზვა, დადგმა და გამოქვეყნება",
  "API reference": "API ცნობარი",
  "REST + webhooks documentation": "REST + ვებჰუკების დოკუმენტაცია",
  "Security & compliance": "უსაფრთხოება და შესაბამისობა",
  "GDPR, data residency, SLAs": "GDPR, მონაცემთა ადგილმდებარეობა, SLA-ები",
  "New ticket composer opening…": "ახალი ტიკეტის ფორმა იხსნება…",
  "Documentation opens in a new tab": "დოკუმენტაცია ახალ ჩანართში გაიხსნება",
  "Thank you — feedback sent to the product team": "მადლობა — უკუკავშირი გაეგზავნა პროდუქტის გუნდს",
  "Opening resource…": "რესურსი იხსნება…",
  "Suggested reply inserted into composer": "შემოთავაზებული პასუხი ჩაისვა ფორმაში",
  "Ticket marked resolved": "ტიკეტი მოინიშნა მოგვარებულად",
  "Reply sent": "პასუხი გაიგზავნა",

  /* ---- Auth pages -------------------------------------------------------------------------------------------------------------------- */
  "Creator Studio": "შემოქმედის სტუდია",
  "Stage apartments in 3D.<br />Share walkable tours.": "დადგი ბინები 3D-ში.<br />გააზიარე სასეირნო ტურები.",
  "Trace floor plans, furnish with real catalog items, and publish a link your clients can explore in first person.":
    "მოხაზე გეგმები, გააწყვე რეალური კატალოგის ნივთებით და გამოაქვეყნე ბმული, რომელსაც კლიენტები პირველი პირიდან დაათვალიერებენ.",
  "2D plan editor → live 3D sync": "2D გეგმის რედაქტორი → ცოცხალი 3D სინქრონი",
  "AI furniture staging": "AI ავეჯის დადგმა",
  "Shareable tour links": "გასაზიარებელი ტურის ბმულები",
  "Spatial staging platform": "სივრცული დადგმის პლატფორმა",
  "Welcome back": "კეთილი იყოს დაბრუნება",
  "Sign in to manage your apartments and publish tours.": "შედი ბინების სამართავად და ტურების გამოსაქვეყნებლად.",
  "Password": "პაროლი",
  "Remember me": "დამიმახსოვრე",
  "Forgot password?": "დაგავიწყდა პაროლი?",
  "Sign in": "შესვლა",
  "New here?": "პირველად ხარ?",
  "Create an account": "ანგარიშის შექმნა",
  "Create account": "ანგარიშის შექმნა",
  "Start staging apartments and sharing walkable 3D tours.": "დაიწყე ბინების დადგმა და სასეირნო 3D ტურების გაზიარება.",
  "Work email": "სამუშაო ელფოსტა",
  "At least 8 characters": "მინიმუმ 8 სიმბოლო",
  "Confirm password": "გაიმეორე პაროლი",
  "Repeat password": "გაიმეორე პაროლი",
  "I agree to the Terms of Service and Privacy Policy": "ვეთანხმები მომსახურების პირობებსა და კონფიდენციალურობის პოლიტიკას",
  "Already have an account?": "უკვე გაქვს ანგარიში?",
  "Passwords do not match.": "პაროლები არ ემთხვევა.",
  "Please accept the terms to continue.": "გასაგრძელებლად დაეთანხმე პირობებს.",
  "Reset password": "პაროლის აღდგენა",
  "Enter your email and we'll send reset instructions.": "შეიყვანე ელფოსტა და გამოგიგზავნით აღდგენის ინსტრუქციას.",
  "Send reset link": "აღდგენის ბმულის გაგზავნა",
  "← Back to sign in": "← შესვლაზე დაბრუნება",
  "Back to sign in": "შესვლაზე დაბრუნება",
  "Check your email": "შეამოწმე ელფოსტა",
  "If an account exists for that address, you'll receive a reset link shortly.": "თუ ამ მისამართზე ანგარიში არსებობს, მალე მიიღებ აღდგენის ბმულს.",
  "Request received. For this demo, use the link if your account exists.": "მოთხოვნა მიღებულია. ამ დემოში გამოიყენე ბმული, თუ ანგარიში არსებობს.",
  "Something went wrong.": "რაღაც შეცდომა მოხდა.",
  "Invalid link": "არასწორი ბმული",
  "This password reset link is missing or expired.": "პაროლის აღდგენის ბმული არასწორია ან ვადაგასულია.",
  "Request a new reset link from the sign-in page.": "მოითხოვე ახალი აღდგენის ბმული შესვლის გვერდიდან.",
  "Request reset link": "აღდგენის ბმულის მოთხოვნა",
  "Choose new password": "აირჩიე ახალი პაროლი",
  "Use at least 8 characters with a mix of letters and numbers.": "გამოიყენე მინიმუმ 8 სიმბოლო ასოებისა და ციფრების კომბინაციით.",
  "New password": "ახალი პაროლი",
  "Update password": "პაროლის განახლება",

  /* Auth store errors */
  "Use a valid email and password (8+ characters).": "გამოიყენე სწორი ელფოსტა და პაროლი (8+ სიმბოლო).",
  "An account with this email already exists.": "ამ ელფოსტით ანგარიში უკვე არსებობს.",
  "Invalid email or password.": "არასწორი ელფოსტა ან პაროლი.",
  "Password must be at least 8 characters.": "პაროლი უნდა იყოს მინიმუმ 8 სიმბოლო.",
  "This reset link has expired. Request a new one.": "აღდგენის ბმულს ვადა გაუვიდა. მოითხოვე ახალი.",
  "Account not found.": "ანგარიში ვერ მოიძებნა.",

  /* Landing page */
  "Landing navigation": "ლენდინგის ნავიგაცია",
  "Features": "ფუნქციები",
  "How it works": "როგორ მუშაობს",
  "Results": "შედეგები",
  "Start free": "უფასო დაწყება",
  "Stage apartments in 3D.<br />Know what buyers see.": "დადგი ბინები 3D-ში.<br />იცოდე რას ხედავს მყიდველი.",
  "Trace floor plans · furnish with AI · publish walkable tours. Then track where buyers look · when they visit · what closes the deal.":
    "ატვირთე გეგმები · მოწყე AI-ით · გამოაქვეყნე სასეირნო ტურები. შემდეგ ნახე სად უყურებენ · როდის ხსნიან · რა ხურავს გარიგებას.",
  "Create free workspace": "უფასო სამუშაო სივრცის შექმნა",
  "Open studio": "სტუდიის გახსნა",
  "No credit card": "ბარათი არ სჭირდება",
  "14-day trial": "14-დღიანი საცდელი",
  "Cancel anytime": "გაუქმება ნებისმიერ დროს",
  "Aspect Overview": "Aspect · მიმოხილვა",
  "Platform metrics": "პლატფორმის მეტრიკები",
  "Everything your team needs to sell spaces": "ყველაფერი, რაც გუნდს სივრცეების გასაყიდად სჭირდება",
  "From empty floor plan to client-ready tour. Staging · sharing · analytics in one glass-clear workspace.":
    "ცარიელი გეგმიდან კლიენტისთვის მზა ტურამდე. დადგმა · გაზიარება · ანალიტიკა ერთ გამჭვირვალ სივრცეში.",
  "Plan to 3D in minutes": "გეგმიდან 3D-მდე წუთებში",
  "Upload a floor plan · trace rooms · publish a walkable tour. No CAD skills required.":
    "ატვირთე გეგმა · დახაზე ოთახები · გამოაქვეყნე სასეირნო ტური. CAD უნარები არ სჭირდება.",
  "AI furniture staging": "AI ავეჯის დადგმა",
  "Furnish empty rooms with one prompt. Real catalog items · confidence scores · instant restaging.":
    "მოწყე ცარიელი ოთახები ერთი პრომპტით. ნამდვილი კატალოგის ნივთები · სანდოობის ქულები · მყისიერი გადადგმა.",
  "First-person tours": "პირველ პირში ტურები",
  "Share a link clients open on any device. They walk through the space like a video game.":
    "გააზიარე ბმული. კლიენტი ნებისმიერ მოწყობილობაზე ხსნის და სივრცეში სეირნობს როგორც თამაშში.",
  "Buyer analytics": "მყიდველის ანალიტიკა",
  "See where viewers linger · when they visit · which rooms drive in-person bookings.":
    "ნახე სად ჩერდებიან · როდის უყურებენ · რომელი ოთახები იწვევს ადგილზე ვიზიტს.",
  "Team workspaces": "გუნდური სივრცეები",
  "Editors · viewers · admins with roles · audit logs · client organizations.":
    "რედაქტორები · მნახველები · ადმინები როლებით · აუდიტის ჟურნალით · კლიენტის ორგანიზაციებით.",
  "API & integrations": "API და ინტეგრაციები",
  "Webhooks · REST API · CRM hooks to plug Aspect into your sales stack.":
    "Webhooks · REST API · CRM კავშირები Aspect-ის შენს გაყიდვების სტეკში ჩასართავად.",
  "Three steps to a published tour": "სამი ნაბიჯი გამოქვეყნებულ ტურამდე",
  "Most teams ship their first staged apartment in under an hour.": "უმეტესი გუნდი პირველ მოწყობილ ბინას ერთ საათზე ნაკლებში აქვეყნებს.",
  "Upload & trace": "ატვირთვა და დახაზვა",
  "Drop a PDF or image floor plan. Draw walls and openings in the 2D editor. 3D syncs live.":
    "ატვირთე PDF ან სურათი. 2D რედაქტორში დახაზე კედლები და გასაღები. 3D ცოცხლად სინქრონდება.",
  "Stage with AI": "დადგმა AI-ით",
  "Pick a style · run staging · tweak placements · publish when you're ready.":
    "აირჩიე სტილი · გაუშვი დადგმა · შეცვალე განლაგება · გამოაქვეყნე როცა მზად ხარ.",
  "Share & measure": "გაზიარება და გაზომვა",
  "Send a tour link. Track sessions · heatmaps · repeat visits from your dashboard.":
    "გაუგზავნე ტურის ბმული. თვალყური ადევნე სესიებს · თერმულ რუკებს · განმეორებით ნახვებს დეშბორდიდან.",
  "Ready to stage your first space?": "მზად ხარ პირველი სივრცის დასადგმელად?",
  "Join teams using Aspect to close faster with immersive tours and buyer intelligence.":
    "შეუერთდი გუნდებს რომლებიც Aspect-ით უფრო სწრაფად ხურავენ გარიგებებს იმერსიული ტურებითა და მყიდველის ანალიტიკით.",
  "Get started free": "უფასო დაწყება",
  "Footer": "ქვედა ზოლი",
  "Studio": "სტუდია",
};

// Keep <html lang> in sync on load
document.documentElement.lang = lang;
